import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StepperActivateEvent } from '@progress/kendo-angular-layout';
import { checkCircleIcon, truckIcon } from '@progress/kendo-svg-icons';
import { finalize, of, switchMap } from 'rxjs';
import { DairyBookingConstants, TrackingConstants } from '../../app-core/constants/common-constants';
import { ContactFormPayload } from '../../app-core/data-model/contact-data-model';
import { ExternalDiaryRequestPayload } from '../../app-core/data-model/external-diary-request-model';
import { OrderUpdateRequestPayload } from '../../app-core/data-model/order-data-model';
import { DiaryBookingRequest } from '../../app-core/interface/dairyBookingReques';
import { LoaderService } from '../../app-core/services/loader.service';
import { CommonService } from './../../app-core/services/common.service';

@Component({
	selector: 'app-update-order-details',
	templateUrl: './update-order-details.component.html',
	styleUrl: './update-order-details.component.scss',
})
export class UpdateOrderDetailsComponent implements OnInit {
	dairyBookingConstants = DairyBookingConstants;
	trackingConstants = TrackingConstants;
	@ViewChild('contactForm') contactForm!: NgForm;
	@ViewChild('mobileCtrl') mobileCtrl!: NgModel;
	@ViewChild('datePickerCtrl') datePickerCtrl!: NgModel;
	src = '/assets/images/calendar.json';
	url: string | null = null;
	truckIcon = truckIcon;
	checkCircleIcon = checkCircleIcon;

	currentStep = 0;
	refNumber = '';
	address = '';
	postCode = '';
	availableSlot: any[] = [];
	allSlots: any[] = [];
	noSlotsAvailable: boolean = false;
	errorMessage = '';
	loading: boolean = false;
	selectedSlotId: string = '';
	contactFormPayload: ContactFormPayload = {
		email: '',
		preferredContactNumber: '',
		mobileNumber: '',
		homeNumber: '',
		workNumber: '',
		specialInstructionsText: '',
		selectedDate: null,
	};
	wismoDetails: any;
	deliveryItem: any;
	serviceItems: any[] = [];

	steps = [
		{ label: 'Contact Information', text: '', svgIcon: checkCircleIcon, isValid: true },
		// { label: "Pre Delivery Survey", text: '', svgIcon: checkCircleIcon, isValid: true },
		{ label: 'Book a date', text: '', svgIcon: checkCircleIcon, isValid: true },
		{ label: 'Delivery Confirmation', text: '', svgIcon: checkCircleIcon, isValid: true },
	];

	delivery = {
		confirmedDate: 'Tue 22-Dec-2020',
		refNumber: '',
		product: '1AXL Test Product',
		orderFrom: 'Very Group',
		services: 'Installation',
	};

	contact = {
		name: 'Miss Laura Potter',
		email: 'Laura.potter@email.co.uk',
		address: '81 Kitt Green Road, Wigan',
		info: '+44 71234526789',
		phone: '+44 71234526721',
		home: '+44 71234526721',
		work: '+44 71234526721',
	};

	constructor(
		private router: Router,
		private commonService: CommonService,
		private route: ActivatedRoute,
		private loaderService: LoaderService
	) { }

	ngOnInit(): void {
		this.updateStepIcons();
		this.route.queryParamMap.subscribe((params) => {
			this.url = params.get('url');
			this.postCode = params.get('postcode') || '';
			this.refNumber = params.get('upi') || '';
			if (this.url === 'confirm-details') {
				this.goToStep(this.currentStep + 1);
			}
		});
		this.getWismoDetails();
	}

	getWismoDetails(): void {
		this.loaderService.show();
		const payload = {
			orderNumber: '',
			upi: this.refNumber,
			postcode: this.postCode,
			requestedBy: '1',
		};
		this.commonService.getWismoDetails(payload).subscribe({
			next: (res) => {
				// Populate contact form payload with fetched data
				this.wismoDetails = res?.arrowxl_wismo_response?.wismo_detail;
				if (this.wismoDetails) {
					this.address = `${this.wismoDetails.address_line_1 || ''} ${this.wismoDetails.address_line_2 || ''} ${this.wismoDetails.city || ''
						} ${this.wismoDetails.post_code || ''}`.trim();
					this.contactFormPayload.email = this.wismoDetails.email_address || '';
					this.contactFormPayload.mobileNumber = this.wismoDetails.mobile_phone || '';
					this.contactFormPayload.homeNumber = this.wismoDetails.home_phone || '';
					this.contactFormPayload.workNumber = this.wismoDetails.work_phone || '';
					this.contactFormPayload.preferredContactNumber = this.wismoDetails.contact_phone || '';
					this.contactFormPayload.specialInstructionsText = this.wismoDetails.delivery_instruction || '';
					this.delivery.refNumber = this.refNumber;
					this.delivery.confirmedDate = this.formateDate(this.wismoDetails?.delivery_date) || '';
					this.delivery.product = this.wismoDetails?.order_line_info?.order_line_info_item[0]?.item_description || '';
					const items = this.wismoDetails?.order_line_info?.order_line_info_item;
					this.deliveryItem = items.find((item: any) => item.line_type === 'Collection' || item.line_type === 'Delivery');
					this.serviceItems = items.filter((item: any) => item.line_type === 'Service');
				}
				this.loaderService.hide();
				this.getDiarySlots();
			},
			error: (err) => {
				if (err.status === 0) {
					this.errorMessage = 'We’re having trouble reaching the server. Please check your connection or try again later.';
				} else {
					this.errorMessage = err || 'Failed to fetch order details. Please try again later.';
				}
				this.loaderService.hide();
			},
		});
	}

	formateDate(dateString: string): string {
		if (!dateString) return '';
		const date = new Date(dateString);
		const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' };
		return date.toLocaleDateString('en-GB', options);
	}

	updateStepIcons(): void {
		this.steps = this.steps.map((step, index) => ({
			...step,
			svgIcon: index === this.currentStep ? this.truckIcon : step.isValid ? this.checkCircleIcon : this.truckIcon,
		}));
	}

	activate(e: StepperActivateEvent): void {
		// this.currentStep = e.step.index;
		this.updateStepIcons();
		// e.preventDefault();
	}

	currentStepChange(e: number): void {
		this.currentStep = e;
	}

	dateFilter = (d: Date | null): boolean => {
		if (!d || !this.availableSlot) return false;

		// Convert availableSlot values to Date objects and compare by date only (ignoring time)
		return this.availableSlot.some((slot) => {
			const slotDate = new Date(slot);
			return slotDate.getFullYear() === d.getFullYear() && slotDate.getMonth() === d.getMonth() && slotDate.getDate() === d.getDate();
		});
	};

	onUpdate(): void {
		if (this.currentStep === 0 && this.mobileCtrl?.control) {
			this.mobileCtrl.control.markAsTouched();
			this.mobileCtrl.control.updateValueAndValidity();
			if (this.mobileCtrl.invalid) return;
		}

		if (this.currentStep === 1 && !this.contactFormPayload.selectedDate) {
			this.datePickerCtrl?.control?.markAsTouched();
			return;
		}

		const date = this.contactFormPayload.selectedDate;
		const formatted = date?.toISOString().split('T')[0];

		const orderUpdatePayload: OrderUpdateRequestPayload = {
			Orders: [
				{
					DeliveryCollectionUpdateOrderHeader: {
						Source: 'AskAXL',
						OrderNumber: this.wismoDetails?.order_number || '',
						OrderSpecialInstruction: this.contactFormPayload.specialInstructionsText || '',
						ShipToAddress: {
							ContactPhone: this.contactFormPayload?.preferredContactNumber || '',
							HomePhone: this.contactFormPayload?.homeNumber || '',
							MobilePhone: this.contactFormPayload?.mobileNumber || '',
							WorkPhone: this.contactFormPayload?.workNumber || '',
							EmailAddress: this.contactFormPayload?.email || '',
						},
						DeliveryCollectionUpdateOrderLine: [],
					},
				},
			],
		};
		this.loaderService.show();
		this.commonService
			.postOrderUpdate(orderUpdatePayload)
			.pipe(
				switchMap((res1) => {
					// Check if delivery_date exists
					if (!this.contactFormPayload?.selectedDate) {
						// Skip diary booking but continue the chain using of(null)
						return of(null);
					}

					const orderBookingPayload : DiaryBookingRequest = {
						diary_booking_request: {
							slot_id: this.selectedSlotId,
							special_instruction: this.contactFormPayload.specialInstructionsText,
							user_id: '1',
							hdnl_order_number: this.wismoDetails.order_number,
							client_order_number: this.wismoDetails.order_number,
							event_source: "AskAXL"
						},
					};

					return this.commonService.postDiaryBooking(orderBookingPayload);
				}),
				finalize(() => {
					this.loaderService.hide();
				})
			)
			.subscribe({
				next: (res2) => {
					if (this.url === 'update-details' && this.wismoDetails?.delivery_date) {
						this.router.navigate(['tracking-history'], {
							queryParams: { upi: this.refNumber, postcode: this.postCode },
						});
					} else {
						this.goToStep(this.currentStep + 1);
						this.getDiarySlots();
					}
				},
				error: (err) => {
					this.errorMessage = err || this.dairyBookingConstants.orderUpdateFailed;
				},
			});
	}

	goToStep(stepIndex: number): void {
		this.currentStep = stepIndex;
		this.activate({ step: { index: stepIndex } } as StepperActivateEvent);
		this.currentStepChange(stepIndex);
	}

	getDiarySlots(): void {
		const slot_from = Number(this.wismoDetails?.slot_from);
		const slot_to = Number(this.wismoDetails?.slot_to);
		const today = new Date();

		const slot_period_start = new Date(today);
		slot_period_start.setDate(today.getDate() + slot_from);

		const slot_period_end = new Date(today);
		slot_period_end.setDate(slot_period_start.getDate() + slot_to);

		const externalDiaryRequestPayload: ExternalDiaryRequestPayload = {
			external_diary_request: {
				request_header: {
					client_account_id: this.wismoDetails?.client_account_id,
					slot_request_id: '',
					latitude: '0',
					longitude: '0',
					stop_duration: this.wismoDetails?.Stop_Duration ?? '0',
					slot_length: '14400',
					delivery_volume: this.wismoDetails?.del_volume ,
					delivery_weight: this.wismoDetails?.del_weight,
					collection_volume: this.wismoDetails?.col_volume,
					collection_weight: this.wismoDetails?.col_weight,
					service_string: '00000',
					request_type: this.wismoDetails?.Request_Type,
					post_code: this.wismoDetails?.post_code,
					slot_period_start: slot_period_start.toISOString().split('T')[0],
					slot_period_end: slot_period_end.toISOString().split('T')[0],
				},
			},
		};
		this.commonService.postDiarySlot(externalDiaryRequestPayload).subscribe({
			next: (res) => {
				this.allSlots = res?.arrowxl_diary_slot_response?.slot_detail?.slots || [];
				// Handle empty slot
				if (!this.allSlots.length) {
					this.noSlotsAvailable = true;
					this.availableSlot = [];
					return;
				}

				this.availableSlot = this.allSlots.map((slot: any) => slot.slot_start_date);
				this.noSlotsAvailable = false;
			},
			error: (err) => {
				this.noSlotsAvailable = true;
			},
		});
	}

	// Step 2: When user selects a date
	onDateSelected(selectedDate: string) {
		const selectedDateStr = this.toLocalDate(selectedDate);

		const selectedSlot = this.allSlots.find((slot: any) => this.toLocalDate(slot.slot_start_date) === selectedDateStr);

		this.selectedSlotId = selectedSlot ? selectedSlot.slot_id : null;
	}

	toLocalDate(date: string | Date): string {
		const d = new Date(date);
		return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
	}

	onCancel(): void {
		this.router.navigate(['/']);
	}

	onBack(): void {
		if (this.currentStep > 0) this.goToStep(this.currentStep - 1);
	}
}
